/**
 * SuperadminBackshopMerge.jsx — AP4
 * Route: /superadmin/backshop-merge
 *
 * Zeigt alle offenen Backshop-Kollisionen aus der Merge-Queue.
 * Superadmin wählt eine von 4 Aktionen pro Kollision:
 *  keep_existing | use_new | merge | keep_both
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GitMerge, CheckCircle2, XCircle, Loader2, Package,
  ArrowLeft, RefreshCw, Inbox, ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MergeDetailDialog } from '../../components/MergeDetailDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function AssetBadge({ ok, label }) {
  return ok
    ? <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full"><CheckCircle2 className="h-3 w-3" />{label}</span>
    : <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full"><XCircle     className="h-3 w-3" />{label} fehlt</span>;
}

function MasterCard({ master, label, highlight }) {
  if (!master) return (
    <div className="flex-1 border-2 border-dashed border-border rounded-xl p-4 text-center text-muted-foreground text-sm">
      {label}: kein Eintrag
    </div>
  );
  return (
    <div className={`flex-1 border-2 rounded-xl p-4 space-y-2 ${highlight ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="font-semibold text-sm">{master.product_name}</p>
      {master.article_number && <p className="text-xs text-muted-foreground">Art.-Nr.: {master.article_number}</p>}
      {master.ean          && <p className="text-xs text-muted-foreground">EAN: {master.ean}</p>}
      <div className="flex gap-1.5 flex-wrap">
        <AssetBadge ok={master.has_image} label="Bild" />
        <AssetBadge ok={master.has_pdf}   label="PDF"  />
        <Badge variant="outline" className="text-[10px]">{master.status}</Badge>
      </div>
    </div>
  );
}

export default function SuperadminBackshopMerge() {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [resolving, setResolving] = useState(null); // queue_id being resolved
  const [mergeItem, setMergeItem] = useState(null); // item für MergeDetailDialog
  const [lastResolvedId, setLastResolvedId] = useState(null); // Edit-Link nach Merge

  // ── Bestehende Duplikate (vor Merge-Queue) ───────────────────────────────
  const [dupGroups, setDupGroups]     = useState([]);
  const [dupLoading, setDupLoading]   = useState(false);
  const [dupMergeItem, setDupMergeItem] = useState(null); // { group, winner, loser }

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/backshop/merge-queue?status=pending`, { headers: authH() });
      setItems(res.data || []);
    } catch { toast.error('Fehler beim Laden der Merge-Queue'); }
    finally { setLoading(false); }
  };

  const fetchDuplicates = async () => {
    setDupLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/backshop/find-duplicates`, { headers: authH() });
      setDupGroups(res.data?.groups || []);
    } catch { toast.error('Fehler beim Duplikat-Scan'); }
    finally { setDupLoading(false); }
  };

  useEffect(() => { fetchQueue(); fetchDuplicates(); }, []);

  const resolve = async (queueId, actionData, productName) => {
    const action = typeof actionData === 'string' ? actionData : actionData.action;
    setResolving(queueId);
    try {
      const res = await axios.post(
        `${API}/superadmin/backshop/merge-queue/${queueId}/resolve`,
        typeof actionData === 'string' ? { action } : actionData,
        { headers: authH() }
      );
      toast.success(`Kollision aufgelöst: ${action === 'keep_existing' ? 'Bestehendes behalten' : action === 'use_new' ? 'Neu verwendet' : action === 'merge' ? 'Zusammengeführt' : 'Beide behalten'}`);
      setMergeItem(null);
      if (action === 'merge' && res.data?.resolved_master_id) {
        setLastResolvedId(res.data.resolved_master_id);
      }
      setItems(prev => prev.filter(i => i.id !== queueId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally { setResolving(null); }
  };

  // Direkt-Merge für pre-existierende Duplikate
  const handleDirectMerge = async (actionData) => {
    const { winner_id, loser_id, merged_fields } = actionData;
    try {
      const res = await axios.post(
        `${API}/superadmin/backshop/direct-merge`,
        { winner_id, loser_id, merged_fields },
        { headers: authH() }
      );
      toast.success(`Zusammengeführt — ${res.data.remapped_customer_products} Kunden-Produkte automatisch umgebogen`);
      setDupMergeItem(null);
      setLastResolvedId(winner_id);
      fetchDuplicates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Direkt-Merge');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/superadmin')} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-primary" />
              Backshop Merge-Queue
            </h1>
            <p className="text-sm text-muted-foreground">Produktkollisionen manuell auflösen</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchQueue(); fetchDuplicates(); }} className="gap-1.5" disabled={loading || dupLoading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading || dupLoading ? 'animate-spin' : ''}`} /> Aktualisieren
          </Button>
        </div>

        {/* Zähler */}
        <div className="flex gap-3 flex-wrap">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl px-4 py-2 text-sm font-medium text-amber-800">
            {items.length} in Queue
          </div>
          {dupGroups.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-xl px-4 py-2 text-sm font-medium text-red-700">
              {dupGroups.length} Duplikat-Gruppe{dupGroups.length !== 1 ? 'n' : ''} im Katalog
            </div>
          )}
        </div>

        {/* ── BESTEHENDE DUPLIKATE (Pre-Queue) ──────────────────────────────── */}
        {dupGroups.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold flex items-center gap-2 text-red-700">
              <XCircle className="h-4 w-4" />
              Bestehende Duplikate im Master-Katalog
              <span className="text-xs font-normal text-muted-foreground ml-1">
                (vor der Merge-Queue angelegt — Artikelnummer oder EAN identisch)
              </span>
            </h2>
            {dupGroups.map((group, gi) => (
              <Card key={gi} className="shadow-sm border-red-200 dark:border-red-800" data-testid={`dup-group-${gi}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="h-4 w-4 text-red-600" />
                    Duplikat: Art.-Nr. {group.match_key.split(':')[1]}
                    <Badge variant="outline" className="ml-auto text-xs">
                      {group.entries.length} identische Einträge
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {group.entries.map((e, ei) => (
                      <div key={e.id} className="border rounded-xl p-3 space-y-1 bg-card">
                        <p className="font-medium text-sm">{e.product_name}</p>
                        {e.article_number && <p className="text-xs text-muted-foreground">Art.-Nr.: {e.article_number}</p>}
                        {e.ean           && <p className="text-xs text-muted-foreground">EAN: {e.ean}</p>}
                        <div className="flex gap-1.5 flex-wrap">
                          <AssetBadge ok={e.has_image} label="Bild" />
                          <AssetBadge ok={e.has_pdf}   label="PDF"  />
                          <Badge variant="outline" className="text-[10px]">{e.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    size="sm" className="gap-1.5 text-xs"
                    disabled={group.entries.length < 2}
                    onClick={() => setDupMergeItem({
                      group,
                      // Baue ein MergeDetailDialog-kompatibles "item" aus den ersten 2 Einträgen
                      id: `dup-${gi}`,
                      collision_reason: `Duplikat Art.-Nr. ${group.match_key.split(':')[1]}`,
                      incoming_submission: {
                        product_name:   group.entries[1]?.product_name,
                        ean:            group.entries[1]?.ean,
                        article_number: group.entries[1]?.article_number,
                        description:    group.entries[1]?.description,
                      },
                      existing_masters: [group.entries[0]],
                    })}
                    data-testid={`dup-merge-btn-${gi}`}
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    Zusammenführen…
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-16 space-y-3 text-muted-foreground">
            <Inbox className="h-12 w-12 mx-auto opacity-30" />
            <p className="text-sm font-medium">Keine offenen Kollisionen</p>
          </div>
        )}

        {/* Kollisions-Karten */}
        {items.map(item => {
          const incoming  = item.incoming_submission || {};
          const existings = item.existing_masters || [];
          const primary   = existings[0] || null;
          const isResolving = resolving === item.id;

          return (
            <Card key={item.id} className="shadow-sm" data-testid={`merge-item-${item.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-4 w-4 text-amber-600" />
                  Kollision: „{incoming.product_name || '–'}"
                  <Badge variant="outline" className="ml-auto text-xs">
                    {item.collision_reason === 'multiple_matches' ? 'Mehrere Treffer' : item.collision_reason}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Vergleich */}
                <div className="flex gap-3 flex-col sm:flex-row">
                  {/* Bestehender Eintrag */}
                  <MasterCard
                    master={primary}
                    label="Bestehend im Katalog"
                    highlight={false}
                  />
                  {/* Neue Einreichung */}
                  <div className="flex-1 border-2 border-primary/30 bg-primary/5 rounded-xl p-4 space-y-2">
                    <p className="text-[10px] font-bold text-primary uppercase tracking-wide">Neu eingereicht</p>
                    <p className="font-semibold text-sm">{incoming.product_name || '–'}</p>
                    {incoming.article_number && <p className="text-xs text-muted-foreground">Art.-Nr.: {incoming.article_number}</p>}
                    {incoming.ean           && <p className="text-xs text-muted-foreground">EAN: {incoming.ean}</p>}
                    {existings.length > 1 && (
                      <p className="text-xs text-amber-700">{existings.length} bestehende Treffer gefunden</p>
                    )}
                  </div>
                </div>

                {/* Aktions-Buttons */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                  <Button
                    variant="outline" size="sm"
                    disabled={isResolving}
                    onClick={() => resolve(item.id, 'keep_existing', incoming.product_name)}
                    data-testid="merge-keep-existing"
                    className="text-xs"
                  >
                    {isResolving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    Bestehendes behalten
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={isResolving}
                    onClick={() => resolve(item.id, 'use_new', incoming.product_name)}
                    data-testid="merge-use-new"
                    className="text-xs"
                  >
                    Neues verwenden
                  </Button>
                  <Button
                    size="sm"
                    disabled={isResolving}
                    onClick={() => setMergeItem(item)}
                    data-testid="merge-merge"
                    className="text-xs gap-1.5"
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    Zusammenführen…
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={isResolving}
                    onClick={() => resolve(item.id, 'keep_both', incoming.product_name)}
                    data-testid="merge-keep-both"
                    className="text-xs"
                  >
                    Beide behalten
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  <strong>Zusammenführen…</strong>: öffnet Feld-für-Feld-Dialog.&nbsp;
                  <strong>Beide behalten</strong>: Einreichung wird als neues Produkt angelegt (unterschiedliche Produkte).
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit-Link nach erfolgreichem Merge */}
      {lastResolvedId && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 z-50"
             data-testid="merge-edit-banner">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Zusammengeführt!</p>
            <p className="text-xs opacity-80">Eintrag jetzt bearbeiten?</p>
          </div>
          <Button
            size="sm" variant="secondary"
            className="ml-2 gap-1.5 text-xs"
            onClick={() => { navigate(`/superadmin/products?edit=${lastResolvedId}`); setLastResolvedId(null); }}
            data-testid="merge-edit-link"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Bearbeiten
          </Button>
          <button onClick={() => setLastResolvedId(null)} className="ml-1 opacity-70 hover:opacity-100">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Feld-für-Feld Merge-Dialog (Queue) */}
      <MergeDetailDialog
        item={mergeItem}
        onConfirm={(actionData) => {
          if (mergeItem) resolve(mergeItem.id, actionData, mergeItem.incoming_submission?.product_name);
        }}
        onCancel={() => setMergeItem(null)}
        loading={!!resolving}
      />

      {/* Feld-für-Feld Merge-Dialog (Direkte Duplikate) */}
      <MergeDetailDialog
        item={dupMergeItem}
        onConfirm={(actionData) => {
          if (!dupMergeItem) return;
          const winner = dupMergeItem.existing_masters[0];
          const loser  = dupMergeItem.group.entries[1];
          handleDirectMerge({
            winner_id:     winner.id,
            loser_id:      loser.id,
            merged_fields: actionData.merged_fields || {},
          });
        }}
        onCancel={() => setDupMergeItem(null)}
        loading={false}
      />
    </DashboardLayout>
  );
}
